import type { StorageUsage } from "@a/overview";
import { cn } from "@u/cn";
import { formatBytes } from "@u/bytes";
import { HardDrive } from "lucide-react";
import { useTranslation } from "react-i18next";

interface StorageUsageBarProps {
  usage: StorageUsage;
  className?: string;
  /** Optional product scope suffix (e.g. "Andar.360") — storage is 360-only. */
  scopeLabel?: string;
  /** Tighter padding/icon to sit alongside compact KPI cards. */
  dense?: boolean;
  /**
   * Optional highlighted portion of `usedBytes` (e.g. this tour's footprint).
   * Rendered as a distinct segment at the start of the fill + a caption line.
   */
  segment?: { bytes: number; label: string };
  /**
   * Render just the bar + legend, without the surface-card wrapper and the
   * icon/label/summary header row — for embedding inside a CardDashboard that
   * already supplies its own chrome and header.
   */
  headerless?: boolean;
}

/**
 * Account-level storage usage bar (used / plan limit). Shared between Overview
 * and Billing. limitBytes === -1 means unlimited (no bar, used only).
 * Storage es exclusivo del producto Andar.360 — `scopeLabel` lo deja explícito.
 */
export function StorageUsageBar({
  usage,
  className,
  scopeLabel,
  dense,
  segment,
  headerless,
}: StorageUsageBarProps) {
  const { t } = useTranslation();
  const { usedBytes, limitBytes } = usage;
  const unlimited = limitBytes === -1;

  const pct = unlimited
    ? 0
    : Math.min(100, Math.round((usedBytes / Math.max(limitBytes, 1)) * 100));
  const over = !unlimited && usedBytes >= limitBytes;
  const near = !unlimited && pct >= 80;

  // Segment (this tour's share) as a % of the plan limit, capped to the fill.
  const segPct =
    segment && !unlimited
      ? Math.min(pct, Math.round((segment.bytes / Math.max(limitBytes, 1)) * 100))
      : 0;
  // This tour vs. the rest, each as a % of the plan limit.
  const otherBytes = segment ? Math.max(0, usedBytes - segment.bytes) : 0;
  const tourPlanPct =
    segment && !unlimited
      ? Math.round((segment.bytes / Math.max(limitBytes, 1)) * 100)
      : 0;
  const otherPlanPct =
    segment && !unlimited
      ? Math.round((otherBytes / Math.max(limitBytes, 1)) * 100)
      : 0;

  const barColor = over
    ? "bg-destructive"
    : near
      ? "bg-warning"
      : // With a segment, the base fill represents "other tours" — lighten it so
        // this tour's solid primary-strong segment reads as clearly distinct.
        segment
        ? "bg-primary/30"
        : "bg-primary";

  const barAndLegend = (
    <>
      {!unlimited && (
        <div className="relative h-2 w-full rounded-full bg-muted/40 overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", barColor)}
            style={{ width: `${pct}%` }}
          />
          {segment && segPct > 0 && (
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary-strong transition-all"
              style={{ width: `${segPct}%` }}
            />
          )}
        </div>
      )}

      {segment && !unlimited && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 label-nano text-muted-foreground uppercase tracking-wide">
          <span>
            <span className="inline-block size-2 rounded-full bg-primary-strong align-middle mr-1.5" />
            {segment.label}: {formatBytes(segment.bytes)} ({tourPlanPct}%)
          </span>
          <span>
            <span className="inline-block size-2 rounded-full bg-primary/30 align-middle mr-1.5" />
            {t("storage_others")}: {formatBytes(otherBytes)} ({otherPlanPct}%)
          </span>
        </div>
      )}
    </>
  );

  // Embedded in a CardDashboard: no surface, no icon/label/summary header —
  // the card owns those. Unlimited + no segment → nothing to show.
  if (headerless) {
    if (unlimited && !segment) return null;
    return (
      <div className={cn("flex flex-col gap-3", className)}>{barAndLegend}</div>
    );
  }

  return (
    <div
      className={cn(
        "surface-card flex flex-col relative overflow-hidden",
        dense ? "px-3.5 py-3 gap-2" : "p-5 gap-3",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              "shrink-0 bg-primary/10 dark:bg-primary/20",
              dense ? "rounded-lg p-1.5" : "rounded-xl p-2.5",
            )}
          >
            <HardDrive size={dense ? 13 : 14} strokeWidth={1.5} className="text-primary" />
          </div>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {t("storage_usage_label")}
            {scopeLabel && (
              <span className="text-muted-foreground/70"> / {scopeLabel}</span>
            )}
          </span>
        </div>
        <span
          className={cn(
            dense
              ? "label-nano text-muted-foreground uppercase tracking-wide"
              : "text-data text-sm",
          )}
        >
          {unlimited
            ? `${formatBytes(usedBytes)} — ${t("storage_unlimited")}`
            : t("storage_of", {
                used: formatBytes(usedBytes),
                limit: formatBytes(limitBytes),
              })}
        </span>
      </div>

      {barAndLegend}
    </div>
  );
}
